import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Clock } from 'lucide-react';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  remainingTime: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  isOpen,
  remainingTime,
  onExtendSession,
  onLogout,
}) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}`;
  };

  // Calcular el porcentaje para la animación del círculo
  const totalWarningTime = 60; // 60 segundos de advertencia (1 minuto)
  const percentage = (remainingTime / totalWarningTime) * 100;
  const circumference = 2 * Math.PI * 54; // radio de 54
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color dinámico basado en el tiempo restante
  const getColor = () => {
    if (remainingTime > 40) return 'text-yellow-600';
    if (remainingTime > 20) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRingColor = () => {
    if (remainingTime > 40) return 'stroke-yellow-500';
    if (remainingTime > 20) return 'stroke-orange-500';
    return 'stroke-red-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 animate-pulse">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <DialogTitle className="text-left">Sesión por expirar</DialogTitle>
              <DialogDescription className="text-left">
                Tu sesión está a punto de cerrarse por inactividad
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-6">
          {/* Contador circular animado */}
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="relative">
              {/* Círculo de fondo */}
              <svg className="transform -rotate-90 w-40 h-40">
                <circle
                  cx="80"
                  cy="80"
                  r="54"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-gray-200"
                />
                {/* Círculo de progreso */}
                <circle
                  cx="80"
                  cy="80"
                  r="54"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className={`${getRingColor()} transition-all duration-1000 ease-linear`}
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Contenido del círculo */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Clock className={`h-8 w-8 mb-1 ${getColor()} animate-pulse`} />
                <div className={`text-5xl font-bold ${getColor()} transition-colors duration-300`}>
                  {formatTime(remainingTime)}
                </div>
              </div>
            </div>
            
            {/* Texto fuera del círculo */}
            <div className="text-center">
              <p className="text-sm text-gray-500 font-medium">
                {remainingTime === 1 ? 'segundo restante' : 'segundos restantes'}
              </p>
            </div>
          </div>
          
          <p className="mt-4 text-center text-sm text-gray-600 font-medium">
            ¿Deseas continuar con tu sesión?
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            onClick={onLogout}
            className="w-full sm:w-auto hover:bg-gray-100"
          >
            Cerrar sesión
          </Button>
          <Button
            onClick={onExtendSession}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all"
          >
            Continuar sesión
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
